import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from "lucide-react";

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { setUser, setOrganization } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const loadPreview = async () => {
      if (!token) {
        setError("This activation link is missing a token.");
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/activate`, { params: { token } });
        setPreview(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "This activation link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    };
    loadPreview();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/activate`,
        { token, password },
        { withCredentials: true }
      );
      setUser(res.data);
      setOrganization(res.data.organization || null);
      navigate("/pa-requests");
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to activate this account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex flex-col">
      <header className="border-b border-slate-800/50 bg-[#0d1321]/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
            <FileText className="w-5 h-5 text-slate-900" />
          </div>
          <div>
            <p className="text-white font-semibold leading-none">Priorath</p>
            <p className="text-[11px] text-slate-400 mt-1">Customer admin activation</p>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-[#0d1321] p-8 space-y-6">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-white">Activate your admin account</h1>
            <p className="text-sm text-slate-400">
              Set a password for your organization admin login. Super-admin credentials are never shared.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Validating invitation...
            </div>
          ) : preview ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="activate-form">
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm">
                <p className="text-emerald-300 font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {preview.practice_name || "Your practice"}
                </p>
                <p className="text-slate-400 mt-1">{preview.full_name} · {preview.email}</p>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="activate-error">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="activate-password" className="text-slate-300">New password</Label>
                <div className="relative">
                  <Input
                    id="activate-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="bg-slate-950 border-slate-800 text-slate-100 pr-10"
                    data-testid="activate-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="activate-confirm" className="text-slate-300">Confirm password</Label>
                <Input
                  id="activate-confirm"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className="bg-slate-950 border-slate-800 text-slate-100"
                  data-testid="activate-confirm-input"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="activate-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Activating...
                  </>
                ) : (
                  "Activate and continue"
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-2 text-red-400 text-sm" data-testid="activate-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error || "This activation link is invalid or expired."}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-700 text-slate-200"
                onClick={() => navigate("/")}
              >
                Go to customer sign in
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ActivateAccount;
